<template>
  <div
    class="max-w-full pl-4 pr-4 h-full m-auto py-2 flex flex-col align-bottom"
  >
    <h2 class="py-4 text-2xl">Adjustments Report</h2>

    <div class="mt-5 sm:flex sm:items-center mb-2">
      <div class="max-w-xs">
        <label for="schedule-date">Schedule Date</label>
        <div class="relative rounded-md shadow-sm">
          <v-date-picker
            id="schedule-date"
            v-model="enteredDateRange"
            mode="range"
            @input="setRowData()"
            color="green"
          />
        </div>
      </div>
      <span
        class="mt-3 inline-flex rounded-md shadow-sm sm:mt-0 sm:ml-3 sm:w-auto"
      >
        <Button
          v-on:click-handler="exportCSV()"
          class="btn btn-primary btn-small sm:mt-6"
          :disabled="!gridHasData"
        >
          Download CSV
          <icon set="outline" name="document-download" class="ml-2 h-6 w-6" />
        </Button>
      </span>
      <span
        class="mt-3 inline-flex rounded-md shadow-sm sm:mt-0 sm:ml-3 sm:w-auto"
      >
        <Button
          v-on:click-handler="showHideItems()"
          class="btn btn-primary btn-small sm:mt-6 pt-5"
          :disabled="!gridHasData"
        >
          {{ showHideAdjustmentText }}
          <icon set="outline" name="eye-off" class="ml-2 h-6 w-6" />
        </Button>
      </span>
    </div>
    <ag-grid-vue
      style="height: 100%; width: 100%"
      class="ag-theme-balham"
      :columnDefs="columnDefs"
      :rowData="rowData"
      :paginationAutoPageSize="false"
      :paginationPageSize="paginationPageSize"
      :pagination="true"
      id="tasks-report"
      :gridOptions="gridOptions"
      @grid-ready="onGridReady"
      :context="context"
      :frameworkComponents="frameworkComponents"
      :defaultColDef="defaultColDef"
      :enableCellTextSelection="true"
      :ensureDomOrder="true"
    >
    </ag-grid-vue>
  </div>
</template>
<script>
import { AgGridVue } from "ag-grid-vue";
import { ApiFactory } from "@/api/ApiFactory.js";
import dateFormatter from "@/mixins/dateFormatter";
import moneyFormatter from "@/mixins/moneyFormatter";
import Button from "@/components/elements/Button.vue";
import AdjustmentsReportOrderLink from "./components/AdjustmentsReportOrderLink";

const OrderAdjustmentsApi = ApiFactory.get("order-adjustments");

export default {
  name: "AdjustmentsReport",
  components: {
    AgGridVue,
    AdjustmentsReportOrderLink, // eslint-disable-line
    Button,
  },
  props: {},
  data() {
    return {
      columnDefs: null,
      rowData: null,
      paginationPageSize: 100,
      frameworkComponents: null,
      gridApi: null,
      columnApi: null,
      gridOptions: null,
      context: null,
      defaultColDef: null,
      overlayLoadingTemplate: true,
      enteredDateRange: {
        start: new Date(new Date().getTime() - 30 * 24 * 60 * 60 * 1000),
        end: new Date(),
      },
      adjustments: {},
      showItems: false,
    };
  },
  computed: {
    gridHasData() {
      return this.rowData && this.rowData.length;
    },
    showHideAdjustmentText() {
      if (this.showItems) {
        return "Hide Adjustment Items";
      }
      return "Show Adjustment Items";
    },
  },
  async created() {
    const response = await ApiFactory.get("feature-flags").isFeatureFlagEnabled("phase3d_care_app");

    if (response.status == 200) {
      if (response.data.body.enabled) {
        await this.$router.push({ name: "Dashboard" });
      }
    }
  },
  beforeMount() {
    this.context = { componentParent: this };
    this.gridOptions = {};

    this.defaultColDef = {
      sortable: true,
      filter: true,
      suppressMovable: true,
      resizable: true,
      flex: 1,
    };

    this.columnDefs = [
      {
        headerName: "Order Number",
        field: "SourceOrderNumber",
        cellRendererFramework: "AdjustmentsReportOrderLink",
        width: 130,
      },
      {
        headerName: "#",
        field: "ItemNumber",
        width: 50,
        minWidth: 50,
        hide: true,
      },
      {
        headerName: "Order Date",
        field: "order_date",
        valueFormatter(params) {
          return dateFormatter.format(params.data.order_date);
        },
        filter: "agDateColumnFilter",
        width: 100,
      },
      {
        headerName: "Date",
        field: "EnteredDate",
        valueFormatter(params) {
          return dateFormatter.format(params.data.EnteredDate);
        },
        filter: "agDateColumnFilter",
        width: 100,
      },
      {
        headerName: "By",
        field: "EnteredBy",
        width: 125,
      },
      {
        headerName: "Comments",
        field: "Comments",
        width: 300,
        minWidth: 300,
        autoHeight: true,
        cellStyle: {
          "white-space": "normal",
        },
      },
      {
        headerName: "Action",
        field: "Action",
        width: 200,
        minWidth: 200,
        autoHeight: true,
        cellStyle: {
          "white-space": "normal",
        },
      },
      {
        headerName: "Reason",
        field: "Reason",
        width: 200,
        minWidth: 200,
        autoHeight: true,
        cellStyle: {
          "white-space": "normal",
        },
      },
      {
        headerName: "Policy",
        field: "Policy",
        width: 200,
        minWidth: 200,
        autoHeight: true,
        cellStyle: {
          "white-space": "normal",
        },
      },
      {
        headerName: "Value",
        field: "Value",
        width: 150,
        type: "rightAligned",
      },
      {
        headerName: "Level",
        field: "AdjustmentLevel",
        width: 80,
        hide: true,
      },
      {
        headerName: "Pre / Post",
        field: "PreOrPostShipment",
        width: 90,
      },
      {
        headerName: "SKU",
        field: "SKU",
        width: 130,
        hide: true,
      },
      {
        headerName: "Quantity",
        field: "Quantity",
        width: 85,
        hide: true,
      },
      {
        headerName: "Price",
        field: "Price",
        width: 120,
        hide: true,
      },
    ];
    this.setRowData();
  },
  mounted() {
    this.gridApi = this.gridOptions.api;
    this.gridColumnApi = this.gridOptions.columnApi;
  },
  methods: {
    setDate(value) {
      if (!value) {
        return null;
      }
      return new Date(value).setHours(0, 0, 0, 0);
    },
    showHideItems() {
      this.showItems = !this.showItems;
      this.setRowData();
      this.columnApi.setColumnsVisible(
        ["SKU", "Quantity", "ItemNumber", "Price", "AdjustmentLevel"],
        this.showItems
      );
      this.gridApi.sizeColumnsToFit();
      this.gridApi.onFilterChanged();
    },
    setRowData() {
      if (this.gridOptions.api) {
        this.gridOptions.api.showLoadingOverlay();
      }
      OrderAdjustmentsApi.list({
        entered_date_start: this.enteredDateRange.start
          .toISOString()
          .slice(0, 10),
        entered_date_end: this.enteredDateRange.end.toISOString().slice(0, 10),
        items: this.showItems,
      }).then((response) => {
        this.adjustments = response.data.body;
        this.getRowData();
      });
    },
    getRowData() {
      this.rowData = this.adjustments.map((row) => {
        return {
          OrderNumber: row.order_number,
          SourceOrderNumber: row.source_order_number,
          ItemNumber: row.item_number,
          EnteredDate: this.setDate(row.created_at),
          order_date: this.setDate(row.order_date),
          EnteredBy: row.entered_by,
          Comments: row.value,
          Action: row.action,
          Reason: row.reason,
          Policy: row.policy,
          Value: this.valueRenderer(row.amount),
          AdjustmentLevel: this.adjustmentLevelRenderer(row),
          PreOrPostShipment: this.preOrPostShipmentRenderer(
            row.pre_or_post_shipment
          ),
          SKU: row.sku,
          Quantity: row.quantity,
          Price: this.valueRenderer(row.price_per_unit),
        };
      });
    },
    onFirstDataRendered(params) {
      params.api.sizeColumnsToFit();
    },
    onGridReady(params) {
      this.gridApi = params.api;
      this.columnApi = params.columnApi;
    },
    valueRenderer(value) {
      if (value) return moneyFormatter.format(value);
    },
    adjustmentLevelRenderer(params) {
      if (params.sku != null) {
        return "Item";
      }
      return "Order";
    },
    preOrPostShipmentRenderer(params) {
      if (params == 1) {
        return "Pre";
      }

      if (params == 2) {
        return "Post";
      }

      return "";
    },
    exportCSV() {
      function formattingFunction(params) {
        if (
          (params.column.getColId() === "EnteredDate" ||
            params.column.getColId() === "order_date") &&
          params.value
        ) {
          return new Date(params.value).toLocaleString().replace(",", "");
        } else {
          return params.value;
        }
      }
      if (!this.gridHasData) return;
      this.gridApi.exportDataAsCsv({
        fileName: this.$options.name + "-" + dateFormatter.format(new Date()),
        processCellCallback: formattingFunction,
      });
    },
  },
  filters: {},
};
</script>
<style lang="scss">
@import "../../../node_modules/ag-grid-community/dist/styles/ag-grid.css";
@import "../../../node_modules/ag-grid-community/dist/styles/ag-theme-balham.css";
</style>
